import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { 
  Home, ShoppingCart, Clock, CreditCard, Users, UserPlus, Wallet, CheckCircle,
  Package, Boxes, Scissors, Truck, BookOpen, ClipboardList, BarChart3, Settings,
  ChevronDown, ChevronRight, LogOut, Menu, X, FileText, DollarSign, RotateCcw, AlertTriangle,
  QrCode, History
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/pos`;

const Layout = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [profile, setProfile] = useState(null);
  const [businessSettings, setBusinessSettings] = useState(null);
  const [userType, setUserType] = useState('owner');
  const [cashierPermissions, setCashierPermissions] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(['sales', 'customers']);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (mobile) {
        setIsSidebarOpen(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  useEffect(() => {
    fetchProfile();
    fetchBusinessSettings();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
      if (response.data.type === 'cashier') {
        setUserType('cashier');
        setCashierPermissions(response.data.permissions || ['billing']);
      } else {
        setUserType('owner');
        setCashierPermissions([]);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchBusinessSettings = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/settings/business`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinessSettings(response.data);
    } catch (error) {
      console.error('Error fetching business settings:', error);
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      // Call logout API to clear server-side token
      await axios.post(`${API}/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      // Continue with logout even if API fails
      console.error('Logout API error:', err);
    }
    
    // Clear local storage
    localStorage.removeItem('pos_token');
    localStorage.removeItem('pos_user_type');
    navigate('/gposp/login');
  };

  const showInventoryFeatures = businessSettings?.inventory_model !== 'no_inventory';

  const permissionToMenu = {
    'billing': '/gposp/pos',
    'estimations': '/gposp/pos',
    'view_dashboard': '/gposp/dashboard',
    'bills_history': '/gposp/bills-history',
    'view_products': '/gposp/products',
    'edit_products': '/gposp/products',
    'stock_management': '/gposp/stock-management',
    'grn_entry': '/gposp/grn-entry',
    'cash_register': '/gposp/cash-register',
    'expenses': '/gposp/expenses',
    'payment_collection': '/gposp/payment-collection',
    'customer_ledger': '/gposp/customer-ledger',
    'supplier_ledger': '/gposp/supplier-ledger',
    'view_reports': '/gposp/reports',
    'manage_customers': '/gposp/settings/customers'
  };

  const hasAccess = (path) => {
    if (userType === 'owner') return true;
    const requiredPermissions = Object.entries(permissionToMenu)
      .filter(([perm, menuPath]) => menuPath === path)
      .map(([perm]) => perm);
    if (path.includes('/gposp/settings')) return false;
    return requiredPermissions.some(perm => cashierPermissions.includes(perm));
  };

  // Menu groups with items
  const menuGroups = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: Home,
      path: '/gposp/dashboard',
      permission: 'view_dashboard',
      single: true
    },
    {
      id: 'sales',
      label: 'Sales',
      icon: ShoppingCart,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      items: [
        { path: '/gposp/pos', label: t('billing'), icon: ShoppingCart, permission: 'billing' },
        { path: '/gposp/cash-register', label: t('cashRegister'), icon: CreditCard, permission: 'cash_register' },
        { path: '/gposp/bills-history', label: t('billsHistory'), icon: Clock, permission: 'bills_history' },
        { path: '/gposp/sales-return', label: 'Sales Return', icon: RotateCcw, ownerOnly: true },
        { path: '/gposp/expenses', label: t('expenses'), icon: Wallet, permission: 'expenses' },
      ]
    },
    {
      id: 'customers',
      label: 'Customers',
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      items: [
        { path: '/gposp/settings/customers', label: 'Customer Settings', icon: UserPlus, permission: 'manage_customers', ownerOnly: true },
        { path: '/gposp/customer-ledger', label: t('customerLedger'), icon: BookOpen, permission: 'customer_ledger' },
        { path: '/gposp/payment-collection', label: 'Payment Collection', icon: Wallet, permission: 'payment_collection' },
        { path: '/gposp/payment-approvals', label: 'Payment Approvals', icon: CheckCircle, ownerOnly: true },
      ]
    },
    {
      id: 'suppliers',
      label: 'Suppliers & Purchase',
      icon: Truck,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      requiresInventory: true,
      items: [
        { path: '/gposp/settings/suppliers', label: 'Supplier Settings', icon: Truck, ownerOnly: true },
        { path: '/gposp/supplier-ledger', label: t('supplierLedger'), icon: BookOpen, permission: 'supplier_ledger' },
        { path: '/gposp/grn-entry', label: t('grnEntry'), icon: ClipboardList, permission: 'grn_entry' },
        { path: '/gposp/grn-return', label: 'GRN Return', icon: RotateCcw, ownerOnly: true },
        { path: '/gposp/grn-payment-history', label: 'GRN Payments', icon: History, ownerOnly: true },
      ]
    },
    {
      id: 'inventory',
      label: 'Inventory',
      icon: Package,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      items: [
        { path: '/gposp/products', label: t('products'), icon: Package, permission: 'view_products' },
        { path: '/gposp/stock-price-management', label: 'Stock & Price Mgmt', icon: Boxes, ownerOnly: true },
        { path: '/gposp/barcode-generator', label: 'Barcode / QR Print', icon: QrCode, ownerOnly: true },
        { path: '/gposp/damaged-stock', label: 'Damaged Stock', icon: AlertTriangle, ownerOnly: true },
        { path: '/gposp/raw-material-usage', label: 'Raw Material Usage', icon: Scissors, requiresInventory: true, permission: 'stock_management' },
      ]
    },
    {
      id: 'reports',
      label: t('reports'),
      icon: BarChart3,
      color: 'text-rose-600',
      bgColor: 'bg-rose-50',
      items: [
        { path: '/gposp/reports', label: 'Sales Reports', icon: BarChart3, permission: 'view_reports' },
        { path: '/gposp/profit-analysis', label: 'Profit Analysis', icon: BarChart3, ownerOnly: true },
        { path: '/gposp/payment-collection-history', label: 'Collection History', icon: FileText, ownerOnly: true },
      ]
    },
    {
      id: 'settings',
      label: t('settings'),
      icon: Settings,
      path: '/gposp/settings',
      ownerOnly: true,
      single: true
    }
  ];

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(g => g !== groupId)
        : [...prev, groupId]
    );
  };

  const isItemVisible = (item) => {
    if (item.requiresInventory && !showInventoryFeatures) return false;
    if (item.ownerOnly && userType !== 'owner') return false;
    if (item.permission && userType === 'cashier') {
      return hasAccess(item.path);
    }
    return true;
  };

  const isGroupVisible = (group) => {
    if (group.requiresInventory && !showInventoryFeatures) return false;
    if (group.ownerOnly && userType !== 'owner') return false;
    if (group.single) {
      if (group.permission && userType === 'cashier') {
        return hasAccess(group.path);
      }
      return true;
    }
    // Check if any item in group is visible
    return group.items?.some(item => isItemVisible(item));
  };

  const isPathActive = (path) => location.pathname === path;
  
  const isGroupActive = (group) => {
    if (group.single) return isPathActive(group.path);
    return group.items?.some(item => isPathActive(item.path));
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 z-50">
        <div className="h-full px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              data-testid="toggle-sidebar-btn"
            >
              {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <span className="bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">GPOS</span>
            </h1>
            {profile && (
              <div className="hidden md:block text-sm">
                <p className="font-semibold text-slate-700">{profile.business_name}</p>
                <p className="text-xs text-slate-500">Store ID: {profile.store_id}</p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            {userType === 'cashier' && (
              <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                Cashier
              </span>
            )}
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
            >
              <LogOut size={18} />
              <span className="text-sm font-medium hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Overlay */}
      {isMobile && isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed left-0 top-16 bottom-0 w-64 bg-white border-r border-slate-200 transition-all duration-300 z-40 flex flex-col ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <nav className="p-3 flex-1 overflow-y-auto" data-testid="sidebar-nav">
          <div className="space-y-1">
            {menuGroups.map(group => {
              if (!isGroupVisible(group)) return null;
              
              const GroupIcon = group.icon;
              const isActive = isGroupActive(group);
              const isExpanded = expandedGroups.includes(group.id);

              // Single item (no submenu)
              if (group.single) {
                return (
                  <Link
                    key={group.id}
                    to={group.path}
                    onClick={() => isMobile && setIsSidebarOpen(false)}
                    data-testid={`menu-${group.id}`}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                      isActive 
                        ? 'bg-violet-50 text-violet-700 font-medium' 
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <GroupIcon size={18} className={isActive ? 'text-violet-600' : 'text-slate-400'} />
                    <span className="text-sm">{group.label}</span>
                  </Link>
                );
              }

              // Group with submenu
              const visibleItems = group.items.filter(isItemVisible);
              if (visibleItems.length === 0) return null;

              return (
                <div key={group.id} className="space-y-0.5">
                  <button
                    onClick={() => toggleGroup(group.id)}
                    data-testid={`menu-group-${group.id}`}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all ${
                      isActive 
                        ? `${group.bgColor || 'bg-violet-50'} ${group.color || 'text-violet-700'} font-medium` 
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <GroupIcon size={18} className={isActive ? group.color || 'text-violet-600' : 'text-slate-400'} />
                      <span className="text-sm">{group.label}</span>
                    </div>
                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                  
                  {isExpanded && (
                    <div className="ml-4 pl-3 border-l-2 border-slate-100 space-y-0.5">
                      {visibleItems.map(item => {
                        const ItemIcon = item.icon;
                        const itemActive = isPathActive(item.path);
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => isMobile && setIsSidebarOpen(false)}
                            data-testid={`menu-item-${item.path.split('/').pop()}`}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm ${
                              itemActive 
                                ? 'bg-violet-50 text-violet-700 font-medium' 
                                : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                            }`}
                          >
                            <ItemIcon size={16} className={itemActive ? 'text-violet-600' : 'text-slate-400'} />
                            <span>{item.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>
        
        {/* Sidebar Footer */}
        <div className="p-3 border-t border-slate-100">
          <div className="text-xs text-slate-400 text-center">
            GPOS v2.0
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`pt-16 transition-all duration-300 ${isSidebarOpen && !isMobile ? 'lg:ml-64' : 'ml-0'}`}>
        <main className="p-4 md:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
